import { useMemo, useState } from "react";
import { Card, DataTable, Badge, Text, Link, EmptyState, BlockStack, InlineStack, Select } from "@shopify/polaris";

const dateOf = (value) => (value ? String(value).substring(0, 10) : "");

const SEVERITY_TONE = { HIGH: "critical", MEDIUM: "attention", LOW: "info" };

const TYPE_LABELS = {
  RELEASE_OVERDUE: "Release overdue",
  EXPEDITE: "Expedite PO",
  PUSH_OUT: "Push out",
  EXCESS_STOCK: "Excess stock",
  NO_CONFIRMED_DATE: "No confirmed date",
  OVERDUE_RECEIPT: "Overdue receipt",
  MISSING_LEAD_TIME: "Missing lead time",
  MISSING_BOM: "Missing BOM",
  BELOW_REORDER_POINT: "Below reorder point",
  DATA_QUALITY: "Data quality",
};

export function ExceptionsList({ exceptions }) {
  const [severityFilter, setSeverityFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");

  const typeOptions = useMemo(() => {
    const present = [...new Set(exceptions.map((exception) => exception.Type))];
    return [{ label: "All types", value: "ALL" }, ...present.map((type) => ({ label: TYPE_LABELS[type] || type, value: type }))];
  }, [exceptions]);

  const filtered = exceptions.filter(
    (exception) => (severityFilter === "ALL" || exception.Severity === severityFilter) && (typeFilter === "ALL" || exception.Type === typeFilter)
  );

  if (!exceptions.length) {
    return (
      <Card>
        <EmptyState heading="No exceptions" image="">
          <p>Nothing needs attention: no expedites, overdue releases or data gaps in the last run.</p>
        </EmptyState>
      </Card>
    );
  }

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack gap="200" blockAlign="center">
          <div style={{ minWidth: "180px" }}>
            <Select
              label="Severity"
              labelHidden
              value={severityFilter}
              onChange={setSeverityFilter}
              options={[
                { label: "All severities", value: "ALL" },
                { label: "High", value: "HIGH" },
                { label: "Medium", value: "MEDIUM" },
                { label: "Low", value: "LOW" },
              ]}
            />
          </div>
          <div style={{ minWidth: "220px" }}>
            <Select label="Type" labelHidden value={typeFilter} onChange={setTypeFilter} options={typeOptions} />
          </div>
          <Text tone="subdued" as="span">{`${filtered.length} of ${exceptions.length} exceptions`}</Text>
        </InlineStack>

        <DataTable
          verticalAlign="middle"
          increasedTableDensity
          columnContentTypes={["text", "text", "text", "text", "text", "numeric", "text"]}
          headings={[<b>Severity</b>, <b>Type</b>, <b>Message</b>, <b>Part</b>, <b>PO</b>, <b>Qty</b>, <b>Dates</b>]}
          rows={filtered.map((exception) => [
            <Badge tone={SEVERITY_TONE[exception.Severity] || "info"}>{exception.Severity}</Badge>,
            <Badge>{TYPE_LABELS[exception.Type] || exception.Type}</Badge>,
            <div style={{ maxWidth: "560px", whiteSpace: "normal" }}>{exception.Message}</div>,
            exception.ProductId ? (
              <Link removeUnderline url={`/products/${exception.ProductId}`}>
                {exception.Productref || exception.ProductId}
              </Link>
            ) : (
              ""
            ),
            exception.BestellingId ? (
              <Link removeUnderline url={`/purchases/${exception.BestellingId}`}>
                {exception.Bestellingnummer || exception.BestellingId}
              </Link>
            ) : exception.WorkOrderId ? (
              <Link removeUnderline url={`/workorders`}>{`WO #${exception.WorkOrderId}`}</Link>
            ) : (
              ""
            ),
            exception.Qty ?? "",
            [dateOf(exception.ReceiptDate), dateOf(exception.NeedDate)].filter(Boolean).join(" → "),
          ])}
        />
      </BlockStack>
    </Card>
  );
}
